import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, input, model, output, viewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import {
  DateFormat,
  DatePipe,
  DefaultTimeStringParseMethod,
  IconComponent,
  TimeStringCannotBeParsedError,
  TimeStringParseMethod,
  TimeStringParser,
} from '@ftr/foundation'
import { LocalTime } from '@js-joda/core'
import { SignalledInputs } from '../../util/signalled-inputs'

export interface InlineLocalTimeInputArgs {
  value: LocalTime | undefined
  placeholder: string
  required: boolean
  readonly: boolean
  disabled: boolean
  hasError: boolean
}

@Component({
  selector: 'ftr-inline-localtime-input',
  templateUrl: './inline-localtime-input.component.html',
  styleUrl: './inline-localtime-input.component.css',
  standalone: true,
  imports: [CommonModule, IconComponent, DatePipe, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineLocalTimeInputComponent implements SignalledInputs<InlineLocalTimeInputArgs> {
  value = model<LocalTime | undefined>()
  required = input(false)
  disabled = input(false)
  readonly = input(false)
  hasError = input(false)
  timeFormat = input(DateFormat.TimeWithSeconds)
  timeStringParseMethods = input<TimeStringParseMethod[]>([DefaultTimeStringParseMethod])
  placeholder = input<string>('H:MM:SS AM')
  label = input<string>('Time')

  validationError = output<string>()
  onFocus = output<Event>()
  onBlur = output<Event>()

  private inputElement = viewChild.required<ElementRef<HTMLInputElement>>('inputElement')

  protected onChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value
    if (value.trim() === '') {
      if (this.required()) {
        this.validationError.emit(`${this.label()} cannot be empty`)
      } else {
        this.value.set(undefined)
        this.inputElement().nativeElement.value = ''
      }
      return
    }

    try {
      const parsed = TimeStringParser.parse(value.trim(), this.timeStringParseMethods())
      this.value.set(parsed)
      // If the value was parsed, we want to update the input to reflect the parsed value
      // This should be handled by the binding to value, however if the resulting value does not change, it does not update
      this.inputElement().nativeElement.value = new DatePipe().transform(parsed, {
        format: this.timeFormat(),
      })
    } catch (err) {
      if (err instanceof TimeStringCannotBeParsedError) {
        this.validationError.emit('Please enter a valid time')
      } else {
        throw err
      }
    }
  }

  protected handleFocus(event: Event): void {
    this.onFocus.emit(event)
  }

  protected handleBlur(event: Event): void {
    this.onBlur.emit(event)
  }
}
